import React, { Fragment, useMemo, } from 'react';
import { isEmpty, orderBy, sortBy, keyBy, } from 'lodash';
import { toast } from 'react-toastify';
import { format as formatDate, addDays, addMonths, startOfDay, endOfDay, } from 'date-fns';
import numeral from 'numeral';

import firebase from '../../firebase';
import { colors, } from '../../shared/config';
import { canUpdateContainer } from '../../shared/abilities';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import ContainerFormModal from '../modals/ContainerFormModal';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useQueryParams from '../hooks/useQueryParams';
import useSimulation from '../hooks/useSimulation';
import ProgressButton from '../ProgressButton';
import ModalButton from '../ModalButton';
import AddInTenantButton from '../AddInTenantButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import QuerySortLabel from '../QuerySortLabel';
import InventoriesHeader from '../InventoriesHeader';
import { fields, } from '../../shared/models/container';
import QuerySelector from '../QuerySelector';
import QueryDateRangeSelector from '../QueryDateRangeSelector';

const { keys, entries } = Object;
const db = firebase.firestore();

export default AdminPage(function AdminInventoriesSchedule (props) {
  const { tenant, user } = props;
  const queryParams = useQueryParams();
  const { isSimulating, isEditable, isMasterLocked, withChanges, changeOf, recordChange, removeChange, } = useSimulation();
  // シミュレーション中はマスタを書き換えず変更内容を記録する
  const canEdit = canUpdateContainer(user) && (isSimulating ? isEditable : !isMasterLocked);
  // シミュレーション中は変更取消ボタンが増えるぶん操作列を広げる
  const labelColWidth = isSimulating ? 113 : 83;
  const departureStartOn = useMemo(_ => queryParams.departureDateRange?.[0] ? startOfDay(new Date(queryParams.departureDateRange[0])) : null, [queryParams.departureDateRange]);
  const departureEndOn = useMemo(_ => queryParams.departureDateRange?.[1] ? endOfDay(new Date(queryParams.departureDateRange[1])) : null, [queryParams.departureDateRange]);
  const arrivalStartOn = useMemo(_ => queryParams.arrivalDateRange?.[0] ? startOfDay(new Date(queryParams.arrivalDateRange[0])) : null, [queryParams.arrivalDateRange]);
  const arrivalEndOn = useMemo(_ => queryParams.arrivalDateRange?.[1] ? endOfDay(new Date(queryParams.arrivalDateRange[1])) : null, [queryParams.arrivalDateRange]);
  const productTypes = useCollectionSubscriptionInTenant(db.collection('productTypes').orderBy('index'));
  const productTypesById = keyBy(productTypes, 'id');
  const productTypeOptions = productTypes.map(_ => ({ value: _.id, label: _.name }));
  const products = useCollectionSubscriptionInTenant(db.collection('products').orderBy('code'));
  const poProducts = products.filter(_ => _.isBody || _.isOption);
  const productOptions = poProducts.map(_ => ({ value: _.id, label: _.code }));
  const productsById = keyBy(products, 'id');
  const suppliers = sortBy(useCollectionSubscriptionInTenant(db.collection('suppliers')), 'name');
  const suppliersById = keyBy(suppliers, 'id');
  const _purchaseOrders = useCollectionSubscriptionInTenant(db.collection('purchaseOrders'));
  const purchaseOrders = useMemo(_ => orderBy(withChanges(_purchaseOrders, 'purchaseOrders'), _ => _.orderedOn?.toDate()), [_purchaseOrders, withChanges]);
  const purchaseOrdersById = keyBy(purchaseOrders, 'id');
  const __containers = useCollectionSubscriptionInTenant(db.collection('containers'));
  const _containers = useMemo(_ => withChanges(__containers, 'containers'), [__containers, withChanges]);
  const containers = useMemo(_ => orderBy(_containers, _ => _.departureDate?.toDate(), queryParams.sort?.direction ?? 'desc'), [_containers, queryParams.sort?.direction]);

  let filteredRowGroups = containers;
  if(departureStartOn != null) {
    filteredRowGroups = filteredRowGroups.filter(_ => _.departureDate.toDate() >= departureStartOn);
  }
  if(departureEndOn != null) {
    filteredRowGroups = filteredRowGroups.filter(_ => _.departureDate.toDate() <= departureEndOn);
  }
  if(arrivalStartOn != null) {
    filteredRowGroups = filteredRowGroups.filter(_ => _.arrivalDate.toDate() >= arrivalStartOn);
  }
  if(arrivalEndOn != null) {
    filteredRowGroups = filteredRowGroups.filter(_ => _.arrivalDate.toDate() <= arrivalEndOn);
  }
  filteredRowGroups = filteredRowGroups.map((rowGroup) => {
    let filteredItems = rowGroup.items;
    if(!isEmpty(queryParams.productTypeIds)) {
      filteredItems = filteredItems.filter(_ => productsById[_.productId]?.productTypeIds?.some(_ => queryParams.productTypeIds.includes(_)));
    }
    if(!isEmpty(queryParams.productIds)) {
      filteredItems = filteredItems.filter(_ => queryParams.productIds.includes(_.productId));
    }
    return {
      ...rowGroup,
      filteredItems,
    };
  }).filter(_ => _.filteredItems.length > 0);

  return (
    <div>
      <div className="admin-containers container-fluid py-5 position-relative bg-white">
        <InventoriesHeader activeTab="containers" />
        <div className='mt-2 d-flex align-items-end flex-wrap gap-2 position-relative' style={{ zIndex: 101, }}>
          <QueryDateRangeSelector label="工場出荷予定日" defaultValue={[departureStartOn, departureEndOn]} paramName="departureDateRange" pickerProps={{ isClearable: true, showYearDropdown: true, dropdownMode: 'select' }} />
          <QueryDateRangeSelector label="倉庫到着予定日" defaultValue={[arrivalStartOn, arrivalEndOn]} paramName="arrivalDateRange" pickerProps={{ isClearable: true, showYearDropdown: true, dropdownMode: 'select' }} />
          <QuerySelector paramName="productTypeIds" width={400} isMulti options={productTypeOptions} label="商品種別で絞込み" />
          <QuerySelector paramName="productIds" width={300} isMulti options={productOptions} label="商品で絞込み" />
        </div>
        <div className="mt-4 d-flex justify-content-end mb-3 position-relative" style={{ zIndex: 100, }}>
          {
            isSimulating ? (
              <ModalButton
                color="primary"
                label="追加"
                icon={<span className="fas fa-plus mr-1" />}
                Modal={ContainerFormModal}
                modalProps={({ toggleModal }) => ({
                  values: {},
                  purchaseOrders, products: poProducts, otherContainers: containers, suppliers,
                  onSubmit: async (values) => {
                    await recordChange('containers', 'create', db.collection('containers').doc().id, values);
                    toast.success('シミュレーションに追加しました');
                    toggleModal(false);
                  },
                })}
                disabled={!canEdit}
              />
            ) : (
              <AddInTenantButton itemRef={db.collection('containers').doc()} FormModal={ContainerFormModal} formProps={{ purchaseOrders, products: poProducts, otherContainers: containers, suppliers, }} disabled={!canEdit} />
            )
          }
        </div>
        <div className="overflow-auto" style={{ maxHeight: '80vh', zIndex: 0 }}>
          {
            containers.length > 0 ? (
              <table className="table table-bordered table-sm sticky-table m-0">
                <thead className="thead-light text-center">
                  <tr>
                    <th style={{ minWidth: labelColWidth }} className="sticky bg-light"></th>
                    <th className="sticky bg-light" style={{ left: labelColWidth, minWidth: 100 }}>
                      コンテナ No.
                    </th>
                    <th style={{ minWidth: 130 }}>仕入先</th>
                    <th style={{ minWidth: 110 }}>
                      <QuerySortLabel field="departureDate" defaultActive defaultDirection="desc">
                        工場出荷予定日
                      </QuerySortLabel>
                    </th>
                    <th style={{ minWidth: 110 }}>倉庫到着予定日</th>
                    <th style={{ minWidth: 150 }}>メモ</th>
                    <th style={{ minWidth: 100 }}>PO No.</th>
                    <th style={{ minWidth: 100 }}>商品コード</th>
                    <th style={{ minWidth: 70 }}>数量</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    filteredRowGroups.map((container) => {
                      const { id, ref, number, supplierId, departureDate, arrivalDate, filteredItems, note, } = container;

                      return (
                        <Fragment key={id}>
                          {
                            filteredItems.map((item, i) => {
                              const { purchaseOrderId, productId, quantity, } = item;
                              const purchaseOrder = purchaseOrdersById[purchaseOrderId];
                              const product = productsById[productId];
                              return (
                                <tr key={id}>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={filteredItems.length} className="bg-white sticky" style={{ minWidth: labelColWidth, maxWidth: labelColWidth }}>
                                          <div className="d-flex gap-1">
                                            <EditButton
                                              size="sm"
                                              label=""
                                              itemRef={ref ?? db.collection('containers').doc(id)}
                                              FormModal={ContainerFormModal}
                                              formProps={{ purchaseOrders, products: poProducts, otherContainers: containers.filter(_ => _.id !== id), suppliers, }}
                                              disabled={!canEdit}
                                              beforeEdit={_ => isSimulating ? container : _}
                                              {...(
                                                isSimulating ? {
                                                  onSubmit: async (values, { toggleModal, }) => {
                                                    await recordChange('containers', container.isSimulationCreated ? 'create' : 'update', id, values);
                                                    toast.success('シミュレーションに記録しました');
                                                    toggleModal?.(false);
                                                  },
                                                } : {}
                                              )}
                                            />
                                            <DeleteButton
                                              size="sm"
                                              label=""
                                              itemRef={ref ?? db.collection('containers').doc(id)}
                                              disabled={!canEdit}
                                              beforeDelete={
                                                isSimulating ? (async _ => {
                                                  await recordChange('containers', 'delete', id);
                                                  toast.success('シミュレーション上で削除しました');
                                                  return false;
                                                }) : undefined
                                              }
                                            />
                                            {
                                              isSimulating && changeOf('containers', id) && (
                                                <ProgressButton size="sm" color="link" className="p-1" title="この変更を取り消す" process={_ => window.confirm('この変更を取り消しますか？') && removeChange('containers', id)} disabled={!isEditable}>
                                                  <span className="fas fa-undo" />
                                                </ProgressButton>
                                              )
                                            }
                                          </div>
                                        </td>
                                        <td rowSpan={filteredItems.length} className="bg-white sticky" style={{ left: labelColWidth }}>
                                          {number}
                                        </td>
                                        <td rowSpan={filteredItems.length}>
                                          {suppliersById[supplierId]?.name}
                                        </td>
                                        <td rowSpan={filteredItems.length}>
                                          {formatDate(departureDate.toDate(), 'yyyy/MM/dd')}
                                        </td>
                                        <td rowSpan={filteredItems.length}>
                                          {formatDate(arrivalDate.toDate(), 'yyyy/MM/dd')}
                                        </td>
                                        <td rowSpan={filteredItems.length} style={{ position: 'relative' }}>
                                          <div style={{ position: 'absolute', inset: 0, whiteSpace: 'pre-line', overflow: 'auto', padding: 4 }}>{note}</div>
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                  <td>
                                    {purchaseOrder?.poNumber}
                                  </td>
                                  <td>
                                    {product?.code}
                                  </td>
                                  <td className="text-right">
                                    {numeral(quantity).format()}
                                  </td>
                                </tr>
                              );
                            })
                          }
                        </Fragment>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                コンテナは未登録です
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
